import { useState } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { Cruise } from '../context/CruiseContext';
import { PublicCruiseView } from '../types/friends';
import { getDateAvailability, getCruisesForDate } from '../utils/calendarOverlayUtils';
import { Calendar as CalendarIcon, X } from 'lucide-react';
import { useDisplayPreferences } from '../hooks/useDisplayPreferences';

interface CalendarOverlayProps {
  userCruises: Cruise[];
  friendCruises: PublicCruiseView[];
  showUserCalendar: boolean;
  showFriendCalendar: boolean;
  onShowUserCalendarChange: (show: boolean) => void;
  onShowFriendCalendarChange: (show: boolean) => void;
  onDateClick?: (date: Date, cruises: Array<Cruise | PublicCruiseView>) => void;
}

const CalendarOverlay = ({
  userCruises,
  friendCruises,
  showUserCalendar,
  showFriendCalendar,
  onShowUserCalendarChange,
  onShowFriendCalendarChange,
  onDateClick,
}: CalendarOverlayProps) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const { formatDate, getCalendarType } = useDisplayPreferences();

  const tileClassName = ({ date, view }: { date: Date; view: string }) => {
    if (view !== 'month') return '';

    const availability = getDateAvailability(
      date,
      userCruises,
      friendCruises,
      showUserCalendar,
      showFriendCalendar
    );

    const classes: string[] = [];

    switch (availability) {
      case 'user-only':
        classes.push('calendar-tile-user-only');
        break;
      case 'friend-only':
        classes.push('calendar-tile-friend-only');
        break;
      case 'both':
        classes.push('calendar-tile-both');
        break;
      case 'available':
        if (showUserCalendar && showFriendCalendar) {
          classes.push('calendar-tile-available');
        }
        break;
    }

    return classes.join(' ');
  };

  const handleDateClick = (value: Date) => {
    setSelectedDate(value);
    if (onDateClick) {
      const userCruisesForDate = showUserCalendar
        ? getCruisesForDate(value, userCruises)
        : [];
      const friendCruisesForDate = showFriendCalendar
        ? getCruisesForDate(value, friendCruises)
        : [];
      onDateClick(value, [...userCruisesForDate, ...friendCruisesForDate]);
    }
  };

  return (
    <div className="calendar-overlay-wrapper space-y-4">
      {/* Toggle Switches */}
      <div className="flex flex-wrap gap-4 items-center">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={showUserCalendar}
            onChange={(e) => onShowUserCalendarChange(e.target.checked)}
            className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500 border-gray-300"
          />
          <span className="text-sm font-medium text-gray-700">Show My Calendar</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={showFriendCalendar}
            onChange={(e) => onShowFriendCalendarChange(e.target.checked)}
            className="w-4 h-4 text-secondary-600 rounded focus:ring-secondary-500 border-gray-300"
          />
          <span className="text-sm font-medium text-gray-700">Show Friend's Calendar</span>
        </label>
      </div>

      {/* Legend - Design system: primary (blue), secondary (teal), accent (gold) */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-gray-800 mb-3">Legend</h3>
        <div className="grid grid-cols-2 gap-2 text-sm">
          {showUserCalendar && (
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-primary-600 rounded"></div>
              <span className="text-gray-700">My Cruises</span>
            </div>
          )}
          {showFriendCalendar && (
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-secondary-600 rounded"></div>
              <span className="text-gray-700">Friend's Cruises</span>
            </div>
          )}
          {showUserCalendar && showFriendCalendar && (
            <>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded calendar-legend-both"></div>
                <span className="text-gray-700">Both Busy</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-accent-100 rounded border-2 border-accent-500"></div>
                <span className="text-gray-700">Both Available</span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Calendar */}
      <div className="calendar-container">
        <Calendar
          onChange={(value) => {
            // If user clears the selection (value === null), setSelectedDate(null)
            // Otherwise, call handleDateClick to properly invoke the onDateClick callback
            if (value === null) {
              setSelectedDate(null);
            } else if (value instanceof Date) {
              handleDateClick(value);
            }
          }}
          value={selectedDate}
          calendarType={getCalendarType()}
          tileClassName={tileClassName}
          className="w-full rounded-lg border-none shadow-sm"
        />
      </div>

      {/* Selected Date Info */}
      {selectedDate && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-blue-900 flex items-center">
              <CalendarIcon className="h-4 w-4 mr-2" />
              {formatDate(selectedDate, { fullMonth: true })}
            </h3>
            <button
              onClick={() => setSelectedDate(null)}
              className="text-primary-600 hover:text-primary-500 transition-colors duration-200"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="space-y-2">
            {showUserCalendar && (
              <div>
                <p className="text-xs font-medium text-blue-800 mb-1">My Cruises:</p>
                {getCruisesForDate(selectedDate, userCruises).length > 0 ? (
                  <ul className="text-xs text-blue-700 space-y-1">
                    {getCruisesForDate(selectedDate, userCruises).map((cruise) => (
                      <li key={cruise.id}>
                        {cruise.shipName} - {cruise.cruiseLine}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs text-blue-600 italic">No cruises</p>
                )}
              </div>
            )}
            {showFriendCalendar && (
              <div>
                <p className="text-xs font-medium text-green-800 mb-1">Friend's Cruises:</p>
                {getCruisesForDate(selectedDate, friendCruises).length > 0 ? (
                  <ul className="text-xs text-green-700 space-y-1">
                    {getCruisesForDate(selectedDate, friendCruises).map((cruise) => (
                      <li key={cruise.id}>
                        {cruise.shipName} - {cruise.cruiseLine}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs text-green-600 italic">No cruises</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarOverlay;

